import React, { useEffect, useMemo } from 'react';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PageLayout, PanelTabs, PageContentPreloader, DefaultButton } from 'common/components';
import { DashboardsPage } from 'common/dashboards';

import {
  UserInfo,
  UserAccountsPanel,
  UserWalletAccountsPanel,
  UserOperations,
  UserBonuses,
  UserHistory,
  UserNotes,
  UserPartnerAccountsPanel,
  UserIssues,
  UserMarks,
  UserKYC,
  UserTrederoCustomKYC,
  TwoFactorAuthorization,
} from '../../_components';
import { getUserData, getUserNotes, startImpersonate } from '../../_redux';

import { staticStyles } from './style';

const UserPage = ({
  match,
  history,
  location,
  profile,
  user,
  userIsLoaded,
  getUserData,
  startImpersonate,
  addons,
  redirect,
  tfAuthEnabled,
  notesCount,
  getUserNotes,
}) => {
  const activeTabSlug = useMemo(
    () => (location.search ? queryString.parse(location.search).tab : 'info'),
    [location.search]
  );

  useEffect(() => {
    getUserData(match.params.id);
  }, [getUserData, match.params.id]);

  useEffect(() => {
    if (redirect) {
      window.location = '/backoffice';
    }
  }, [redirect]);

  useEffect(() => {
    if (profile && (profile.perms.viewNotes || profile.perms.fullAccessNotes)) {
      getUserNotes({ id: match.params.id, search: { page_size: 10, note_type: 'all' } });
    }
  }, [getUserNotes, profile, match.params.id]);

  return (
    <PageLayout captionSlug="boUser" extraCaptionText={match.params.id} showReload={false}>
      {(profile.perms.impersonateUser || profile.perms.managerImpersonateUser || profile.isAdmin) &&
        user.email !== profile.email &&
        !user.isAdmin &&
        (profile.isAdmin || !user.isStaff) && (
          <div className="UserPage__impersonate">
            <DefaultButton
              textId="loginAsUser"
              filled
              onClick={() => {
                startImpersonate({ userId: match.params.id });
                window.location.replace('/');
              }}
            />
          </div>
        )}
      <PageContentPreloader ready={userIsLoaded} type="boUserInfo">
        <PanelTabs activeTabSlug={activeTabSlug} location={location} history={history}>
          <UserInfo textId="boUserInfo" slug="info" match={match} />
          <UserAccountsPanel textId="boUserAccounts" slug="accounts" id={match.params.id} />
          {addons.includes('walletAccounts') && (
            <UserWalletAccountsPanel slug="walletAccounts" textId="boUserWalletAccounts" id={match.params.id} />
          )}
          {user.isPartner && (
            <UserPartnerAccountsPanel textId="boUserPartnerAccounts" slug="partner_accounts" id={match.params.id} />
          )}
          {profile.perms.viewDashboards && user.isPartner && (
            <DashboardsPage
              textId="boDashboards"
              slug="dashboards"
              match={match}
              partnerId={user.id}
              sampleType="partner"
            />
          )}
          {((profile.perms.viewDeposit && profile.perms.viewWithdraw) ||
            (profile.perms.managerViewDeposit && profile.perms.managerViewWithdraw)) && (
            <UserOperations textId="userPayments" slug="payments" />
          )}
          {!addons.includes('trederoCustomKyc') && (profile.perms.changeKYC || profile.perms.managerChangeKYC) && (
            <UserKYC textId="justKYC" slug="kyc" />
          )}
          {addons.includes('trederoCustomKyc') && (profile.perms.changeKYC || profile.perms.managerChangeKYC) && (
            <UserTrederoCustomKYC textId="justKYC" slug="kyc" />
          )}
          {addons.includes('bonusesSystem') && (profile.perms.viewBonuses || profile.perms.managerViewBonuses) && (
            <UserBonuses textId="boBonuses" slug="bonuses" />
          )}
          <UserHistory textId="boUserHistory" slug="history" id={match.params.id} />
          {(profile.perms.viewNotes || profile.perms.managerViewNotes) && (
            <UserNotes textId="boUserNotes" slug="notes" tabCount={notesCount} id={match.params.id} />
          )}
          {profile.perms.fullAccessSupport && <UserIssues textId="boIssues" slug="issues" id={match.params.id} />}
          <UserMarks textId="boUtmMarks" slug="marks" id={match.params.id} />
          {(tfAuthEnabled || user.tfaSettings?.enabled) && (
            <TwoFactorAuthorization textId="justTwoFactorAuth" slug="2fa" id={match.params.id} />
          )}
        </PanelTabs>
      </PageContentPreloader>

      <style jsx>{staticStyles}</style>
    </PageLayout>
  );
};

UserPage.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  userIsLoaded: PropTypes.bool.isRequired,
  getUserData: PropTypes.func.isRequired,
  startImpersonate: PropTypes.func.isRequired,
  addons: PropTypes.array.isRequired,
  redirect: PropTypes.bool,
  tfAuthEnabled: PropTypes.bool.isRequired,
  getUserNotes: PropTypes.func.isRequired,
  notesCount: PropTypes.number,
};

UserPage.defaultProps = {
  redirect: false,
  notesCount: null,
};

const ConnectedUserPage = connect(
  state => ({
    profile: state.user.profile,
    user: state.backoffice.users.user,
    userIsLoaded: state.backoffice.users.userIsLoaded,
    addons: state.interfaceConfig.addons,
    redirect: state.backoffice.common.redirect,
    tfAuthEnabled: state.interfaceConfig.tfAuthSettings.enabled,
    notesCount: state.backoffice.users.notes?.all_notes_count,
  }),
  {
    getUserData: id => getUserData.request(id),
    getUserNotes: data => getUserNotes.request(data),
    startImpersonate: data => startImpersonate.request(data),
  }
)(UserPage);

export default ConnectedUserPage;
